import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { decamelize } from 'humps'
import { intersection, isArray } from 'lodash'
import { forwardRef } from 'react'
import { DropdownProps } from 'shared/components/Dropdown/Dropdown'
import { Text } from 'shared/components/Text/Text'
import { primaryBlack, royalBlue } from 'shared/theme/colors'
import styled from 'styled-components'

import { FlexBox } from '../../../components/Base/FlexBox'
import { CheckboxDropdown } from '../../../components/Dropdown/CheckboxDropdown'
import { truthy } from '../../../helpers/typeHelper'
import {
  getDefaultJobStatusDetailValue,
  JOB_STATUS_DICT,
  JOB_STATUS_DICT_KEYS,
  JobStatusDetail,
} from '../../../models/job'

interface ProfileJobStatusFilterDropownProps {
  statusFilter: JobStatusDetail[]
  onChange: (status: JobStatusDetail[]) => void
  isDisabled?: boolean
  hiddenOptions?: JOB_STATUS_DICT_KEYS[]
}

const Value = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${royalBlue};
    position: relative;
    font-size: 24px;
    top: -1px;
    left: -5px;
  }

  svg:hover {
    color: ${primaryBlack};
  }
`

const filterHiddenOptions = (option: string, hiddenOptions?: JOB_STATUS_DICT_KEYS[]) => {
  if (!hiddenOptions) return true

  return (
    !hiddenOptions?.includes(decamelize(option).toUpperCase() as JOB_STATUS_DICT_KEYS) &&
    !hiddenOptions
      .flatMap((key) => JOB_STATUS_DICT[key])
      ?.includes(decamelize(option).toUpperCase() as JobStatusDetail)
  )
}

export const ProfileJobStatusFilterDropown = forwardRef(function ProfileJobStatusFilterDropown(
  {
    statusFilter,
    onChange,
    isDisabled,
    hiddenOptions,
  }: ProfileJobStatusFilterDropownProps & Omit<DropdownProps<unknown>, 'options' | 'onChange'>,
  ref: any
) {
  function handleChangeFactory(newStatusFilter: JobStatusDetail | JobStatusDetail[]) {
    return (isEnabled: unknown) => {
      let currentValue = statusFilter || getDefaultJobStatusDetailValue()

      // Group of options
      if (isArray(newStatusFilter)) {
        if (isEnabled && !intersection(currentValue, newStatusFilter).length) {
          currentValue = [...currentValue, ...newStatusFilter]
        }

        if (!isEnabled && intersection(currentValue, newStatusFilter).length) {
          currentValue = currentValue.filter((v) => !newStatusFilter.includes(v))
        }
      } else {
        if (isEnabled && !currentValue.includes(newStatusFilter)) {
          currentValue = [...currentValue, newStatusFilter]
        }

        if (!isEnabled && currentValue.includes(newStatusFilter)) {
          currentValue = currentValue.filter((v) => v !== newStatusFilter)
        }
      }

      if (currentValue.length === 0) return

      onChange(currentValue.filter((option) => filterHiddenOptions(option, hiddenOptions)))
    }
  }

  const emptyStatusFilter = statusFilter === null
  const isCurrentSelected = Boolean(
    emptyStatusFilter || intersection(statusFilter, JOB_STATUS_DICT.CURRENT).length
  )
  const isFutureSelected = Boolean(
    emptyStatusFilter || intersection(statusFilter, JOB_STATUS_DICT.FUTURE).length
  )
  const isPendingSelected = Boolean(
    emptyStatusFilter || intersection(statusFilter, JOB_STATUS_DICT.PENDING).length
  )
  const isPastSelected = Boolean(emptyStatusFilter || intersection(statusFilter, JOB_STATUS_DICT.PAST).length)
  const isPaymentIssuedSelected = Boolean(
    emptyStatusFilter || statusFilter?.includes(JobStatusDetail.PAYMENT_ISSUED)
  )
  const isAbortedSelected = Boolean(emptyStatusFilter || statusFilter?.includes(JobStatusDetail.ABORTED))
  const isCanceledSelected = Boolean(emptyStatusFilter || statusFilter?.includes(JobStatusDetail.CANCELED))

  function getFilterOptions() {
    return [
      {
        key: 'current',
        label: 'Current',
        value: isCurrentSelected,
        onChange: handleChangeFactory(JOB_STATUS_DICT.CURRENT),
      },
      {
        key: 'future',
        label: 'Future',
        value: isFutureSelected,
        onChange: handleChangeFactory(JOB_STATUS_DICT.FUTURE),
      },
      {
        key: 'pending',
        label: 'Pending',
        value: isPendingSelected,
        onChange: handleChangeFactory(JOB_STATUS_DICT.PENDING),
      },
      {
        key: 'past',
        label: 'Past',
        value: isPastSelected,
        onChange: handleChangeFactory(JOB_STATUS_DICT.PAST),
      },
      {
        key: 'paymentIssued',
        label: 'Payment issued',
        value: isPaymentIssuedSelected,
        onChange: handleChangeFactory(JobStatusDetail.PAYMENT_ISSUED),
        Wrap: NestedCheckbox,
      },
      {
        key: 'aborted',
        label: 'Aborted',
        value: isAbortedSelected,
        onChange: handleChangeFactory(JobStatusDetail.ABORTED),
        Wrap: NestedCheckbox,
      },
      {
        key: 'canceled',
        label: 'Canceled',
        value: isCanceledSelected,
        onChange: handleChangeFactory(JobStatusDetail.CANCELED),
        Wrap: NestedCheckbox,
      },
    ].filter((option) => filterHiddenOptions(option.key, hiddenOptions))
  }

  function renderOpener() {
    return [
      isCurrentSelected && 'current',
      isFutureSelected && 'future',
      isPendingSelected && 'pending',
      isPastSelected && 'past',
    ]
      .filter(truthy)
      .filter((v) => filterHiddenOptions(v, hiddenOptions))
      .join(', ')
  }

  return (
    <CheckboxDropdown ref={ref} options={getFilterOptions()} align="left" width={250} maxHeight={300}>
      <Value>
        <FlexBox gap="10px" alignItems="center">
          <Text
            variant="header3"
            fontColor={isDisabled ? primaryBlack : royalBlue}
            styles="text-transform: capitalize;"
          >
            {renderOpener()}
          </Text>
          {!isDisabled ? <ArrowDropDownIcon /> : null}
        </FlexBox>
      </Value>
    </CheckboxDropdown>
  )
})

const NestedCheckbox = styled.div`
  padding-left: 20px;
`
